import {useParams} from 'react-router-dom'
import {Divider} from 'antd'
import useStore from 'base/store'
import { PermissionType } from 'common/types'
import styles from './ProjectAboutStats.module.scss'
import {useGetRequest} from 'modules/request'
import {useOpen} from 'common/hooks'
import {
  Text,
  Box,
  ButtonLink,
  Block,
  Modal,
  IconStat,
  Loader,
  CentsMoney,
  // MoneyBanknoteExchangeSecondIcon, MoneyCoinsIcon, HouseExchangeIcon,
  ArrowToIcon,
} from 'common/components'
import {totalNumberFromArray, formatMoney} from 'common/helpers'
import {
  SecurityItems,
  useGetProject,
  useGetSecurityItems,
  SecurityItem,
  isLVROverLimit,
} from '..'

interface ProjectAboutStatsProps {
  requestUuid: string
}

interface StatProps {
  title: string
  value: string | JSX.Element
  label: string | JSX.Element
}

interface SecurityItemsButtonProps {
  securityItems: SecurityItem[]
}

interface BonusTooltipProps {
  bonusAmountPerYear: any
}

const BonusTooltip = ({bonusAmountPerYear}: BonusTooltipProps) => {
  const entries: [string, number][] = Object.entries(bonusAmountPerYear)

  return (
    <>
      {entries.map(([year, amount]) => (
        <p key={year}>
          <strong>{year}:</strong> {formatMoney(amount, {cents: true})}
        </p>
      ))}
    </>
  )
}

const Stat = ({title, value, label}: StatProps) => (
  <Box align="left" weight="medium">
    <Text color="grey" mb={2}>
      {title}
    </Text>
    <Box size="2xl" mb={2}>
      {value}
    </Box>
    <Text color="grey" size="xs">
      {label}
    </Text>
  </Box>
)

const SecurityItemsButton = ({securityItems}: SecurityItemsButtonProps) => {
  const [isOpen, handleOpen, handleClose] = useOpen()

  return (
    <>
      <ButtonLink icon={<ArrowToIcon />} onClick={handleOpen}>
        security items
      </ButtonLink>
      <Modal
        open={isOpen}
        title="Security items"
        width={960}
        onCancel={handleClose}
      >
        <SecurityItems items={securityItems} />
      </Modal>
    </>
  )
}

export default function ProjectAboutStats({
  requestUuid,
}: ProjectAboutStatsProps) {
  const permissions: PermissionType[] = useStore((state) => state.permissions)
  const params = useParams()
  const {data: project} = useGetProject(params.uuid)
  const {data: request} = useGetRequest(requestUuid)
  const {data: securityItems} = useGetSecurityItems(params.uuid, !!params.uuid)

  if (!request || !securityItems) return <Loader />

  const activeItems = securityItems.filter(
    (item) => item.status !== 'discharged'
  )

  const isLvrOverLimit = isLVROverLimit(project)

  return (
    <div className={styles.root}>
      <Block className={styles.block} type="grey">
        <IconStat
          // align="center"
          // icon={<MoneyBanknoteExchangeSecondIcon />}
          icon={<></>}
          title="IRR"
          value={`${project.xirr}%`}
          label={`loan IRR ${request.irr}%`}
        />
        <Divider />
        <Stat
          title="Interest & fees"
          value={<CentsMoney value={project.cashflowSum} />}
          label="forecasted"
        />
      </Block>
      <Block className={styles.block} type="grey">
        <IconStat
          align="center"
          // icon={<MoneyCoinsIcon />}
          icon={<></>}
          iconColor="green"
          title="Current income"
          value={<CentsMoney value={project.currentIncome} />}
          label="current interest & fees"
        />
        <Divider />
        <div className={styles.grid}>
          <Stat
            title="Interest"
            value={<CentsMoney value={project.totalInterest} />}
            label={`${request.interest}%`}
          />
          <Stat
            title="Establishment fee"
            value={<CentsMoney value={project.estFees} />}
            label={`${request.establishFee || request.extensionFee}%`}
          />
          <Stat
            title="Line fee"
            value={<CentsMoney value={project.totalFees} />}
            label={`${request.lineFee}%`}
          />
          { 
            (!!permissions.includes('view bonus')) &&
          <Stat
            title="Bonus"
            label={`${request.bonus ?? '- '}%`}
            value={
              (
                (request?.bonus !== null)             && (request?.bonus !== undefined) && 
                (project?.bonusAmount !== null)       && (project?.bonusAmount !== undefined) && 
                (project?.bonusAmountPerYear !==null) && (project?.bonusAmountPerYear !== undefined) 
              ) ?
              <CentsMoney
                value={project.bonusAmount}
                tooltip={
                  <BonusTooltip
                    bonusAmountPerYear={project.bonusAmountPerYear}
                  />
                }
              />
              :
              <CentsMoney
                value={0}
                tooltip={
                  <BonusTooltip
                    bonusAmountPerYear={0}
                  />
                }
              />
            }
          />
          }
        </div>
      </Block>
      <Block className={styles.block} type="grey">
        <IconStat
          isError={isLvrOverLimit}
          // align="center"
          // icon={<HouseExchangeIcon />}
          icon={<></>}
          title="LVR"
          value={`${project.lvr}%`}
          label={`${isLvrOverLimit ? 'over the ' : ''}max ${request.lvrMax}%`}
        />
        <Divider />
        <Stat
          title="Security value"
          value={formatMoney(
            totalNumberFromArray(
              activeItems.map((item) => item.improvementValue + item.landValue)
            )
          )}
          label={
            (!permissions.includes('edit security')) ? null :
            <SecurityItemsButton securityItems={securityItems} />
          }
        />
      </Block>
    </div>
  )
}
